import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'

const API_URL = process.env.REACT_APP_BACKEND_URL

const SchoolOfferPage = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  const [offer, setOffer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [validationError, setValidationError] = useState(null)

  useEffect(() => {
    loadOffer()
  }, [token, user])

  const loadOffer = async () => {
    setLoading(true)
    setError(null)
    setValidationError(null)

    try {
      const response = await fetch(`${API_URL}/api/school-offer/${token}`)

      if (!response.ok) {
        if (response.status === 404) {
          setError('not_found')
        } else {
          setError('unknown')
        }
        setLoading(false)
        return
      }

      const data = await response.json()
      setOffer(data)
      
      // Validate offer
      validateOffer(data)

    } catch (err) {
      console.error('Error loading offer:', err)
      setError('unknown')
    } finally {
      setLoading(false)
    }
  }

  const validateOffer = (offerData) => {
    // Check status
    if (offerData.status === 'used') {
      setValidationError('used')
      return
    }
    
    if (offerData.status === 'cancelled') {
      setValidationError('cancelled')
      return
    }
    
    if (offerData.status !== 'active') {
      setValidationError('invalid_status')
      return
    }

    // Check expiration
    const validUntil = new Date(offerData.valid_until)
    const now = new Date()
    
    if (now > validUntil) {
      setValidationError('expired')
      return
    }

    // Check if user is logged in
    if (!user) {
      setValidationError('login_required')
      return
    }

    // Check if user email matches school email
    if (user.email !== offerData.school_email) {
      setValidationError('email_mismatch')
      return
    }

    // All validations passed
    setValidationError(null)
  }

  const handleProceedToPayment = async () => {
    // Phase 3: Navigate to Stripe checkout
    try {
      setLoading(true)
      
      // Get auth token from Supabase session (correct way)
      let authToken = null
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
        if (sessionError) {
          console.error('[Offer Checkout] Session error:', sessionError.message)
        } else if (sessionData?.session?.access_token) {
          authToken = sessionData.session.access_token
        }
      } catch (sessionErr) {
        console.error('[Offer Checkout] Session fetch error:', sessionErr.message || String(sessionErr))
      }
      
      if (!authToken) {
        alert('Prihlásenie vypršalo. Prosím, prihláste sa znova.')
        navigate('/login', { state: { from: `/school-offer/${token}` } })
        return
      }
      
      const response = await fetch(`${API_URL}/api/school-offer-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          token: token,  // offer token from URL param
          origin_url: window.location.origin
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        alert(errorData.detail || 'Chyba pri vytváraní platby')
        return
      }
      
      const data = await response.json()
      
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url
      } else {
        alert('Chyba: Nepodarilo sa vytvoriť platobnú session')
      }
      
    } catch (err) {
      console.error('Checkout error:', err)
      alert('Nastala chyba pri vytváraní platby. Skúste to prosím neskôr.')
    } finally {
      setLoading(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam ponuku...</p>
        </div>
      </div>
    )
  }

  // Error: Offer not found
  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Ponuka nenájdená</h1>
            <p className="text-gray-600 mb-6">
              Táto ponuka neexistuje.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            >
              Späť na hlavnú stránku
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Error: Unknown error
  if (error === 'unknown') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Chyba</h1>
            <p className="text-gray-600 mb-6">
              Pri načítaní ponuky došlo k chybe. Skúste to prosím neskôr.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            >
              Späť na hlavnú stránku
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Validation Error: Used
  if (validationError === 'used') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Ponuka už bola použitá</h1>
            <p className="text-gray-600 mb-6">
              Táto ponuka už bola použitá a nie je možné ju použiť znova.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
            >
              Späť na hlavnú stránku
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Validation Error: Expired
  if (validationError === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⏰</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Ponuka vypršala</h1>
            <p className="text-gray-600 mb-6">
              Platnosť tejto ponuky vypršala.
            </p>
            {offer && (
              <p className="text-sm text-gray-500 mb-6">
                Platná bola do: {new Date(offer.valid_until).toLocaleDateString('sk-SK', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
            >
              Späť na hlavnú stránku
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Validation Error: Cancelled
  if (validationError === 'cancelled') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Ponuka bola zrušená</h1>
            <p className="text-gray-600 mb-6">
              Táto ponuka bola zrušená.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
            >
              Späť na hlavnú stránku
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Validation Error: Login required
  if (validationError === 'login_required') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🔐</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Prihlásenie potrebné</h1>
            <p className="text-gray-600 mb-6">
              Pre pokračovanie sa prihláste školským účtom, pre ktorý bola táto ponuka vytvorená.
            </p>
            {offer && (
              <p className="text-sm text-gray-500 mb-6">
                Ponuka je určená pre: <strong>{offer.school_email}</strong>
              </p>
            )}
            <button
              onClick={() => navigate('/login', { state: { from: `/school-offer/${token}` } })}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            >
              Prihlásiť sa
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Validation Error: Email mismatch
  if (validationError === 'email_mismatch') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Neplatný účet</h1>
            <p className="text-gray-600 mb-4">
              Táto ponuka nie je dostupná pre prihlásený účet.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Ponuka je určená pre: <strong>{offer.school_email}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Prihlásený účet: <strong>{user?.email}</strong>
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/login', { state: { from: `/school-offer/${token}` } })}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                Prihlásiť sa iným účtom
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Späť
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Success state: All validations passed
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        
        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-8 px-8">
            <div className="text-center">
              <div className="text-5xl mb-4">🎉</div>
              <h1 className="text-3xl font-bold mb-2">
                Individuálna ponuka pre vašu školu
              </h1>
              <p className="text-blue-100">
                Pre vašu školu sme pripravili individuálnu cenu za prístup do platenej verzie finančnej gramotnosti.
              </p>
            </div>
          </div>

          {/* Offer Details */}
          <div className="p-8">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-6">
              <div className="grid gap-4">
                <div>
                  <span className="text-sm font-semibold text-gray-700">Škola:</span>
                  <p className="text-lg font-bold text-gray-900 mt-1">{offer.school_name}</p>
                </div>
                
                <div className="border-t border-blue-200 pt-4">
                  <span className="text-sm font-semibold text-gray-700">Cena:</span>
                  <p className="text-3xl font-bold text-green-600 mt-1">
                    {offer.custom_price_eur} {offer.currency}
                  </p>
                </div>

                <div className="border-t border-blue-200 pt-4">
                  <span className="text-sm font-semibold text-gray-700">Platnosť ponuky do:</span>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    {new Date(offer.valid_until).toLocaleDateString('sk-SK', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
              <div className="flex items-start">
                <span className="text-yellow-600 text-xl mr-3">⚠️</span>
                <div>
                  <p className="text-sm font-semibold text-yellow-900 mb-1">Dôležité upozornenie</p>
                  <p className="text-sm text-yellow-800">
                    Táto ponuka je určená len pre školu, pre ktorú bola vytvorená. Ponuku je možné použiť len jedenkrát.
                  </p>
                </div>
              </div>
            </div>

            {/* What's included */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Čo získate:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-green-500 text-xl">✓</span>
                  <span className="text-gray-700">Prístup do platenej verzie finančnej gramotnosti</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 text-xl">✓</span>
                  <span className="text-gray-700">Všetky interaktívne scenáre pre žiakov</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 text-xl">✓</span>
                  <span className="text-gray-700">Platnosť 1 rok od zakúpenia</span>
                </li>
              </ul>
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <button
                onClick={handleProceedToPayment}
                className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold text-lg rounded-xl hover:from-blue-700 hover:to-green-700 transition shadow-lg"
              >
                Pokračovať na platbu
              </button>
            </div>

            {/* Info text */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Po kliknutí na tlačidlo budete presmerovaní na bezpečnú platobnú bránu Stripe
            </p>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-800 text-sm"
          >
            ← Späť na hlavnú stránku
          </button>
        </div>

      </div>
    </div>
  )
}

export default SchoolOfferPage
